import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import errorIcon from "../../assets/error-triangle.svg";
import { useAppContext } from "../../AppContext";
import { trackEvent } from "../../mixpanel";
import productRoute from "../../services/productRoute";
import adminRoute from "../../services/adminRoute";
import orderRoute from "../../services/orderRoute";

const DeleteProductModal = ({ item, itemType, actionType, onSuccess }) => {
  const { showDeleteModal, setShowDeleteModal, showToast } = useAppContext();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Service hooks
  const {
    deleteProduct,
    deleteCategory,
    deactivateBoxTemplate,
    deactivateVoucherTemplate,
    deactivateSubscription,
    deactivateVoucher,
  } = productRoute();

  const { deactivatePromo, activatePromo, setReviewPublic } = adminRoute();
  const { deleteOrder, deactivateLocation } = orderRoute();

  const closeModal = () => setShowDeleteModal(false);

  const toggleStatus = (status) => (status === "active" ? "inactive" : "active");

  const handleDelete = () => {
    const id = item?.id || item?._id;
    const status = item?.status;

    const actions = {
      "product": () => deleteProduct(id, setShowDeleteModal, setLoading, showToast),

      "order": () => deleteOrder(id, setShowDeleteModal, setLoading, showToast),

      "category": () => {
        const body = { status: toggleStatus(status || "inactive") };
        deleteCategory(id, body, setShowDeleteModal, setLoading, showToast);
      },

      "promo": () => {
        const fn = status === "active" ? deactivatePromo : activatePromo;
        fn(id, setShowDeleteModal, setLoading, showToast);
      },

      "location": () => {
        const body = { status: toggleStatus(status || "inactive") };
        deactivateLocation(id, body, setShowDeleteModal, setLoading, showToast);
      },

      "boxTemplate": () => deactivateBoxTemplate(id, setShowDeleteModal, setLoading, showToast),

      "voucherTemplate": () =>
        deactivateVoucherTemplate(id, setShowDeleteModal, setLoading, showToast),

      "subscription": () => {
        const body = { status: toggleStatus(status || "inactive") };
        deactivateSubscription(id, body, setShowDeleteModal, setLoading, showToast);
      },

      "voucher": () => {
        const body = { isActive: !item?.isActive };
        deactivateVoucher(id, body, setShowDeleteModal, setLoading, showToast);
      },

      "review": () =>
        setReviewPublic(id, !item?.public, setShowDeleteModal, setLoading, showToast, onSuccess),

      "customer": closeModal,
      "customer-account": closeModal,
      "admin": closeModal,
    };

    actions[itemType]?.();
  };

  const renderTitleAndMessage = () => {
    const isInactive = item?.status === "inactive" || item?.isActive === false;
    const statusText = isInactive ? "Activate" : "Deactivate";

    switch (itemType) {
      case "product":
        return (
          <>
            <h4>Are you sure you want to delete this product?</h4>
            <h4 className="mt-3 fw-bold">{item?.productName}</h4>
          </>
        );

      case "order":
        return (
          <>
            <h4>Delete Order</h4>
            <h4>You are about to delete this order. Proceed?</h4>
          </>
        );

      case "category":
        return (
          <>
            <h4>{statusText} Category</h4>
            <h4>
              You are about to {statusText.toLowerCase()} <br />
              <b>{item?.categoryName}</b>
              <br />
              Are you sure you want to proceed?
            </h4>
          </>
        );

      case "promo":
      case "subscription":
      case "location":
        return (
          <>
            <h4>
              {statusText}{" "}
              {itemType === "subscription"
                ? "Subscription Box"
                : itemType === "promo"
                  ? "Promotion"
                  : "Location"}
            </h4>
            <h4>
              You are about to {statusText.toLowerCase()} <br />
              <b>{item?.name}</b>
              <br />
              Are you sure you want to proceed?
            </h4>
          </>
        );

      case "voucher":
        return (
          <>
            <h4>{statusText} Voucher</h4>
            <h4>
              You are about to {statusText.toLowerCase()} this voucher.
              <br />
              Are you sure you want to proceed?
            </h4>
          </>
        );

      case "boxTemplate":
      case "voucherTemplate":
        return (
          <>
            <h4>Delete {itemType === "boxTemplate" ? "Box Template" : "Voucher Template"}</h4>
            <h4>
              You are about to delete <br />
              <b>{item?.groupName || item?.name}</b>
              <br />
              Are you sure you want to proceed?
            </h4>
          </>
        );

      case "review": {
        const reviewAction = item?.public ? "Deactivate" : "Activate";
        const reviewerName =
          item?.user?.name?.trim() ||
          [item?.user?.firstName, item?.user?.lastName]
            .map((part) => part?.trim())
            .filter(Boolean)
            .join(" ");
        return (
          <>
            <h4>{reviewAction} Review</h4>
            <h4>
              You are about to {reviewAction.toLowerCase()} this review
              {reviewerName ? (
                <>
                  {" "}
                  by <br />
                  <b>{reviewerName}</b>
                </>
              ) : null}
              <br />
              Are you sure you want to proceed?
            </h4>
          </>
        );
      }

      case "customer":
      case "admin":
        return (
          <>
            <h4>Deactivate User</h4>
            <h4>
              You are about to deactivate a user account:
              <br />
              <b>{item?.email}</b>
              <br />
              Are you sure you want to proceed?
            </h4>
          </>
        );

      case "customer-account":
        return (
          <>
            <h4>Delete Account</h4>
            <h4>
              You are about to delete the user account:
              <br />
              <b>{item?.email}</b>
            </h4>
          </>
        );

      default:
        return null;
    }
  };

  const renderActionButton = () => {
    const isInactive = item?.status === "inactive" || item?.isActive === false;
    const statusText = isInactive ? "Activate" : "Deactivate";
    const colorVariant = isInactive ? "success" : "danger";

    if (["category", "subscription", "promo", "location"].includes(itemType)) {
      return (
        <Button variant={colorVariant} onClick={handleDelete} disabled={loading}>
          {loading ? "Processing..." : `Yes, ${statusText}`}
        </Button>
      );
    }

    if (itemType === "review") {
      return (
        <Button
          variant={item?.public ? "danger" : "success"}
          onClick={handleDelete}
          disabled={loading}
        >
          {loading ? "Processing..." : `Yes, ${item?.public ? "Deactivate" : "Activate"}`}
        </Button>
      );
    }

    if (itemType === "voucher") {
      return (
        <Button variant={colorVariant} onClick={handleDelete} disabled={loading}>
          {loading ? "Processing..." : `Yes, ${statusText}`}
        </Button>
      );
    }

    if (
      ["product", "customer-account", "voucherTemplate", "boxTemplate", "order"].includes(
        itemType
      ) ||
      actionType === "delete"
    ) {
      return (
        <Button variant="danger" onClick={handleDelete} disabled={loading}>
          {loading ? "Deleting..." : "Yes, Delete"}
        </Button>
      );
    }

    return (
      <Button variant={colorVariant} onClick={handleDelete} disabled={loading}>
        {loading ? "Processing..." : `Yes, ${statusText}`}
      </Button>
    );
  };

  return (
    <Modal show={showDeleteModal} onHide={closeModal} centered>
      <Modal.Header closeButton />
      <Modal.Body className="d-flex flex-column justify-content-center text-center gap-3 px-5 pb-5">
        <div>
          <img src={errorIcon} alt="Error" style={{ width: "70px" }} />
        </div>

        <div>{renderTitleAndMessage()}</div>

        <div className="d-flex gap-3 justify-content-center">
          <Button variant="outline-danger" onClick={closeModal}>
            No, Cancel
          </Button>
          {renderActionButton()}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DeleteProductModal;
